import {useEffect, useId, useLayoutEffect, useRef, useState, type HTMLAttributes, type ReactNode} from "react";
import {
	Action, Actions, Chip, Close, Content, Hit, Line, Root, Row, Secondary, Title, type SlideOutAccent,
} from "./SlideOutCard.styles";
import type {SlideOutStackedSpot} from "./slideOutCards";

export interface SlideOutCardProps extends HTMLAttributes<HTMLElement> {
	/** Which family this card belongs to; decides every colour and nothing else. */
	accent: SlideOutAccent;
	/** Out or tucked: owned by the panel through `useSlideOutCards`, never held here. */
	open: boolean;
	/** The panel is lifted for this card's arrival; the peel frame reads it as `data-peel-card-lift`. */
	lifting: boolean;
	/** Where this card sits beside the panel on a wide screen: from the anchor down. */
	offsetTop: number;
	/** Where it sits under the panel in the stacked layout. */
	stacked: SlideOutStackedSpot;
	title: string;
	/** The card's ONE call to action. */
	actionLabel: string;
	/** A fuller accessible name for it, when the print is shorter than what a screen reader should hear. */
	actionAria?: string;
	onAction: () => void;
	/** The tucked chip's accessible name — the one control a reader meets while the card is closed. */
	showLabel: string;
	/** The ×'s accessible name. */
	tuckLabel: string;
	/** What the chip carries: the warning's triangle, the upsell's arrow. */
	glyph: ReactNode;
	testId: string;
	onOpen: () => void;
	onTuck: () => void;
	/** The card's measured height, so the panel can space the stack by what is actually drawn. */
	onHeight: (height: number) => void;
	/** The body between the title and the action row: the limit card's meter, the upsell card's line. */
	children?: ReactNode;
	/** The upsell card's single supporting line, rendered in the shell so both cards share its type. */
	line?: string;
	/**
	 * A way to decline beside the action, the same size as it: the upgrade moments' "Not now"
	 * (kitecorp/transferify-ui#2909). Absent on every other card, which keep the × alone.
	 */
	secondaryLabel?: string;
	onSecondary?: () => void;
	/**
	 * The action row without the decorative glyph chip beside the button, so the button stands alone:
	 * the limit-wall offer card (kitecorp/transferify-ui#3197), whose chip is the whole of its tucked
	 * state and nothing in its open one. The two family members keep the chip in the row.
	 */
	actionAlone?: boolean;
}

/**
 * The card that slides out from behind the send panel — the shell both members of the family share
 * (kitecorp/transferify-ui#2468, #2278).
 *
 * A dumb view over the panel's `useSlideOutCards` controls plus whatever its family hands down. It holds
 * no open/tucked state, because it is portalled and a remount would reset it — the rule and the defect
 * are on `slideOutCards.ts`. The two things it measures for itself are where its top edge sits and how
 * tall it is; both are reported upwards so the panel can space the stack.
 *
 * It renders into the peel frame BESIDE the panel (CreateFlow hands `TransferPanel` the host), never
 * inside it: a card behind the panel must be the panel's sibling in the same stacking context, because
 * a descendant cannot paint under its own ancestor's background, and the panel's 4px lift is a
 * transform-free `top` on the frame's card wrapper so no `position: fixed` sheet inside is re-parented.
 *
 * <b>It never blocks Send.</b> Nothing here touches the draft or the submit button.
 */
export function SlideOutCard({
	accent, open, lifting, offsetTop, stacked, title, actionLabel, actionAria, onAction, showLabel, tuckLabel,
	glyph, testId, onOpen, onTuck, onHeight, children, line, secondaryLabel, onSecondary, actionAlone = false, ...rest
}: SlideOutCardProps) {
	const [anchorTop, setAnchorTop] = useState(0);
	const [height, setHeight] = useState(0);
	const root = useRef<HTMLElement>(null);
	const content = useRef<HTMLDivElement>(null);
	const titleId = useId();

	// Level with the file row: measured against the frame the card is positioned in, and re-measured
	// whenever the panel's height changes (a notice appears, files are added) — the anchor moves with it.
	useLayoutEffect(() => {
		const frame = root.current?.closest<HTMLElement>("[data-peel-frame]");
		if (!frame) return;
		const align = () => {
			const anchor = frame.querySelector<HTMLElement>("[data-slide-out-anchor]");
			setAnchorTop(anchor ? anchor.getBoundingClientRect().top - frame.getBoundingClientRect().top : 0);
		};
		align();
		const observer = new ResizeObserver(align);
		observer.observe(frame);
		window.addEventListener("resize", align);
		return () => { observer.disconnect(); window.removeEventListener("resize", align); };
	}, []);

	// The card's own height comes from its CONTENT, which keeps its width and its layout whether the card
	// is out or tucked. Measuring the ROOT instead would read the chip's 44px the moment it tucks, and the
	// stack under it would jump to the chip's spacing before the tuck had visibly started.
	useLayoutEffect(() => {
		const body = content.current;
		if (!body) return;
		const measure = () => setHeight(body.offsetHeight);
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(body);
		return () => observer.disconnect();
	}, []);

	useEffect(() => onHeight(height), [height, onHeight]);

	return (
		<Root
			ref={root}
			$accent={accent}
			$open={open}
			$top={anchorTop + offsetTop}
			$stackedTop={stacked.top}
			$stackedLeft={stacked.left}
			$height={height}
			aria-labelledby={titleId}
			data-testid={testId}
			data-state={open ? "open" : "closed"}
			data-peel-card-lift={lifting || undefined}
			{...rest}
		>
			{!open && <Hit $accent={accent} type="button" aria-label={showLabel} onClick={onOpen}/>}
			{!open && <Chip $accent={accent} $tucked aria-hidden="true">{glyph}</Chip>}
			<Content ref={content} $open={open} aria-hidden={!open} inert={!open}>
				<Close $accent={accent} type="button" aria-label={tuckLabel} onClick={onTuck}>
					<svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true"><path d="m4 4 8 8m0-8-8 8" stroke="currentColor" strokeWidth="2"/></svg>
				</Close>
				<Title id={titleId}>{title}</Title>
				{line !== undefined && <Line $accent={accent}>{line}</Line>}
				{children}
				{secondaryLabel === undefined || onSecondary === undefined ? (
					<Row>
						{!actionAlone && <Chip $accent={accent} aria-hidden="true">{glyph}</Chip>}
						<Action $accent={accent} type="button" aria-label={actionAria} onClick={onAction}>{actionLabel}</Action>
					</Row>
				) : (
					<Actions>
						<Action $accent={accent} type="button" aria-label={actionAria} onClick={onAction}>{actionLabel}</Action>
						<Secondary $accent={accent} type="button" onClick={onSecondary}>{secondaryLabel}</Secondary>
					</Actions>
				)}
			</Content>
		</Root>
	);
}
